import { isPendingStatus } from '@/components/chat/AttachmentChips';
import { COMPOSER_MAX_ATTACHMENTS, ChatComposer } from '@/components/chat/ChatComposer';
import { ChatFooterNote } from '@/components/chat/ChatFooterNote';
import { FileDropOverlay } from '@/components/chat/FileDropOverlay';
import { GreetingHero } from '@/components/chat/GreetingHero';
import { SidebarOpenerButton } from '@/components/layout/SidebarOpenerButton';
import { useFileDropUpload } from '@/hooks/useFileDropUpload';
import { api } from '@/lib/api';
import { useAppSettings } from '@/lib/appSettings';
import type { ComposerAttachment } from '@/lib/attachment';
import { useAuth } from '@/lib/auth';
import { useChatModel } from '@/lib/chatModel';
import { setPendingMessage } from '@/lib/pendingChat';
import type { FileUpload, FileUploadList } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

export function ChatIndex() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const appSettings = useAppSettings();
  const [error, setError] = useState<string | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const [busy, setBusy] = useState(false);

  const { isDragging } = useFileDropUpload({
    maxAttachments: COMPOSER_MAX_ATTACHMENTS,
    currentCount: attachments.length,
    allowedExts: appSettings.data?.uploadAllowedExts,
    onFilesDropped: (files) => {
      setDropError(null);
      setAttachments((prev) => {
        const slots = COMPOSER_MAX_ATTACHMENTS - prev.length;
        const take = files.slice(0, Math.max(0, slots));
        const additions: ComposerAttachment[] = take.map((file) => ({
          kind: 'pending',
          localId: crypto.randomUUID(),
          file,
          phase: 'pending',
        }));
        return [...prev, ...additions];
      });
    },
    onError: (msg) => setDropError(msg),
  });

  const createMut = useMutation({
    mutationFn: (body: import('@innermate/shared').CreateChatSessionRequest) =>
      api.chatCreateSession(body),
  });

  // 라이브러리 모드로 선택한 `uploaded` 칩은 다이얼로그 선택 시점의 동결 status 라 인제스트가
  // 끝나도 갱신되지 않는다. 비종료 파일이 있을 때만 폴링해 composer 칩 status 를 ready/failed 로
  // 전환한다 (전송 시 Session 으로 navigate 하므로 짧게만 동작). 세션 스코프 없는 `['files', null]`.
  const filesQuery = useQuery({
    queryKey: ['files', null],
    queryFn: () => api.fileList(undefined),
    enabled: attachments.some((a) => a.kind === 'uploaded'),
    staleTime: 30_000,
    refetchInterval: (q) =>
      (q.state.data?.files ?? []).some((f) => isPendingStatus(f.status)) ? 3000 : false,
  });
  const fileById = useMemo(() => {
    const m = new Map<string, FileUpload>();
    for (const f of filesQuery.data?.files ?? []) m.set(f.id, f);
    return m;
  }, [filesQuery.data]);

  const model = useChatModel();

  const onSend = async (content: string) => {
    setError(null);
    setBusy(true);
    try {
      const snapshot = attachments;
      const pendings = snapshot.filter(
        (a): a is Extract<ComposerAttachment, { kind: 'pending' }> => a.kind === 'pending',
      );
      const uploadedFileObjs = snapshot
        .filter(
          (a): a is Extract<ComposerAttachment, { kind: 'uploaded' }> => a.kind === 'uploaded',
        )
        .map((a) => a.file);
      const alreadyUploadedIds = uploadedFileObjs.map((f) => f.id);

      let newUploadedIds: string[] = [];
      let newUploadedFiles: FileUpload[] = [];
      if (pendings.length > 0) {
        setAttachments((cur) =>
          cur.map((a) =>
            a.kind === 'pending' ? { ...a, phase: 'uploading' as const, error: undefined } : a,
          ),
        );
        const results = await Promise.all(
          pendings.map(async (p) => {
            try {
              const uploaded = await api.fileUpload(p.file);
              return { ok: true as const, localId: p.localId, uploaded };
            } catch (err) {
              return {
                ok: false as const,
                localId: p.localId,
                error: err instanceof Error ? err.message : '업로드 실패',
              };
            }
          }),
        );
        const successById = new Map<string, FileUpload>();
        const failedById = new Map<string, string>();
        for (const r of results) {
          if (r.ok) successById.set(r.localId, r.uploaded);
          else failedById.set(r.localId, r.error);
        }
        if (failedById.size > 0) {
          setAttachments((cur) =>
            cur.map((a) => {
              if (a.kind !== 'pending') return a;
              const err = failedById.get(a.localId);
              return err ? { ...a, phase: 'failed' as const, error: err } : a;
            }),
          );
          setError(`첨부 ${failedById.size}개 업로드 실패. 칩에서 다시 시도해 주세요.`);
          setBusy(false);
          return;
        }
        newUploadedFiles = Array.from(successById.values());
        newUploadedIds = newUploadedFiles.map((f) => f.id);
        // 방금 업로드한 파일을 `['files', null]` 캐시에 즉시 주입. navigate 후 Session.tsx 의
        // MessageBubble.useAttachedFiles 가 prefix 매칭으로 이걸 발견해 "(삭제됨)" 폴백을 방지.
        const additions = newUploadedFiles;
        qc.setQueryData<FileUploadList>(['files', null], (old) => {
          if (!old) return { files: additions, storageReady: true };
          const exists = new Set(old.files.map((f) => f.id));
          const fresh = additions.filter((f) => !exists.has(f.id));
          return { ...old, files: [...fresh, ...old.files] };
        });
        qc.invalidateQueries({ queryKey: ['files'] });
      }

      const fileIds = [...alreadyUploadedIds, ...newUploadedIds];
      // 응답 시간 측정 기준 시각 — 세션 생성 직전 (사용자가 send 한 직후) 으로 잡는다.
      // 첨부 업로드 시간은 응답 시간에 포함하지 않는다.
      const startedAt = Date.now();
      const sel = model.selected;
      const selectedEndpointId = sel?.kind === 'endpoint' ? sel.id : undefined;
      const selectedBondingId = sel?.kind === 'bonding' ? sel.id : undefined;
      const selectedExtended =
        sel?.supportsExtendedThinking === true ? model.extendedThinking : undefined;
      // 세션 생성 시점에 사용자 선택 모델로 stamp — 안 그러면 시스템 기본값으로 만들어졌다가
      // Session.tsx 진입 시 picker 가 default 로 깜빡인다.
      const { id } = await createMut.mutateAsync({
        endpointId: selectedEndpointId,
        bondingId: selectedBondingId,
        extendedThinking: selectedExtended,
      });
      // 첨부 업로드 객체를 pending 큐로 Session 에 넘긴다. Session 이 knownFiles 누적 맵에 merge 해
      // 첫 턴 진행 중 user 메시지 칩이 "(삭제됨)" 대신 실제 파일명/상태로 표시된다 (캐시 seed 는
      // refetch 와 race 해 무효 — 객체를 직접 전달하는 게 안전).
      setPendingMessage(id, content, fileIds, {
        endpointId: selectedEndpointId,
        bondingId: selectedBondingId,
        extendedThinking: selectedExtended,
        startedAt,
        files: [...uploadedFileObjs, ...newUploadedFiles],
      });
      setAttachments([]);
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      navigate({ to: '/chat/$sessionId', params: { sessionId: id } });
    } catch (e) {
      setError(e instanceof Error ? e.message : '세션 생성 실패');
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="flex-1 flex flex-col relative">
      <FileDropOverlay visible={isDragging} />
      {/* hero 배경에 콘텐츠가 거의 없으므로 좌상단 absolute 햄버거가 가릴 게 없음 — 예외 케이스. */}
      <div className="absolute top-3 left-3 z-20">
        <SidebarOpenerButton />
      </div>
      <div className="flex-1 grid place-items-center px-6">
        <GreetingHero userName={user.displayName} />
      </div>
      <div className="px-6 pb-8">
        <div className="max-w-3xl mx-auto">
          {error ? (
            <div className="mb-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
              {error}
            </div>
          ) : null}
          {dropError ? (
            <div className="mb-3 text-sm text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
              {dropError}
            </div>
          ) : null}
          <ChatComposer
            onSend={onSend}
            pending={busy || createMut.isPending}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            liveById={fileById}
            modelPicker={
              appSettings.data?.userModelSelectionEnabled === false
                ? undefined
                : {
                    models: model.models,
                    selected: model.selected,
                    extendedThinking: model.extendedThinking,
                    onSelect: model.setSelected,
                    onToggleThinking: model.setExtendedThinking,
                  }
            }
          />
          <ChatFooterNote />
        </div>
      </div>
    </div>
  );
}
